import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, X, Search, FileText, ArrowRightLeft, Trash2, ChevronDown, ChevronUp, Link, Paperclip, History, MoreHorizontal, ChevronRight, Pencil } from 'lucide-react'; // PLENARIUM:TRAMITE_GAVETA
import PareceresMateria from '../components/PareceresMateria'; // PLENARIUM:COMISSOES
import { excluirComConfirmacao } from '../components/ConfirmarExclusao'; // PLENARIUM:LIXEIRA_MATERIAS
import RetificacoesAta from '../components/RetificacoesAta'; // PLENARIUM:ATA_RETIFICACAO
import { BotaoMateriasTeste } from '../components/HomologacaoTeste'; // PLENARIUM:HOMOLOGACAO

const TIPO_LABEL = {
  PROJETO_LEI: 'Projeto de Lei', PROJETO_RESOLUCAO: 'Proj. Resolução',
  PROJETO_DECRETO: 'Proj. Decreto', REQUERIMENTO: 'Requerimento',
  MOCAO: 'Moção', INDICACAO: 'Indicação', OUTROS: 'Outros',
  ATA_ANTERIOR: 'Ata da Sessão Anterior' /* PLENARIUM:ATA_MATERIA */
};

const ETAPA_LABEL = {
  RECEBIMENTO: { label: 'Recebimento', cor: 'badge-azul', icon: '📥' },
  COMISSAO:    { label: 'Comissão',    cor: 'badge-amarelo', icon: '👥' },
  PLENARIO:    { label: 'Plenário',    cor: 'badge-indigo', icon: '🏛️' },
  PROMULGADO:  { label: 'Promulgado',  cor: 'badge-verde', icon: '✅' },
  ARQUIVADO:   { label: 'Arquivado',   cor: 'badge-cinza', icon: '📦' },
  DEVOLVIDO:   { label: 'Devolvido',   cor: 'badge-vermelho', icon: '↩️' },
};

const STATUS_LABEL = {
  EM_TRAMITACAO: { label: 'Em Tramitação', cor: 'badge-azul' },
  APROVADO:      { label: 'Aprovado',      cor: 'badge-verde' },
  REPROVADO:     { label: 'Reprovado',     cor: 'badge-vermelho' },
  ARQUIVADO:     { label: 'Arquivado',     cor: 'badge-cinza' },
  DEVOLVIDO:     { label: 'Devolvido',     cor: 'badge-vermelho' },
  PROMULGADO:    { label: 'Promulgado',    cor: 'badge-verde' },
};

// Tipos de documento que uma materia pode ter. Batem com o enum
// TipoAnexo do banco -- mudar aqui exige mudar la tambem.
const TIPO_ANEXO_LABEL = {
  PROJETO:          'Projeto / Texto principal',
  PARECER_COMISSAO: 'Parecer da Comissão',
  PARECER_RELATOR:  'Parecer do Relator',
  PARECER_JURIDICO: 'Parecer Jurídico', // PLENARIUM:COMISSOES
  EMENDA:           'Emenda',
  SUBSTITUTIVO:     'Substitutivo',
  REDACAO_FINAL:    'Redação Final',
  OFICIO:           'Ofício',
  OUTROS:           'Outros',
};

// O corpo da requisicao aceita 10MB (express.json no server.js) e o
// base64 infla o arquivo em cerca de um terco. 5MB de arquivo viram
// uns 6,7MB de texto -- cabe com folga. Nao aumente sem aumentar la.
const LIMITE_ANEXO_MB = 5;

const VAZIO_ANEXO = { nome: '', tipo: 'PROJETO', etapa: '', descricao: '', arquivo: '', nomeArquivo: '' };

const VAZIO = { tipo: 'PROJETO_LEI', ementa: '', descricao: '', autor: '', vereadorId: '', arquivoUrl: '', autoria: { tipo: 'VEREADORES', vereadorIds: [], comissaoId: '', texto: '' } }; // PLENARIUM:AUTORIA
const VAZIO_TRAMITE = { etapa: 'PLENARIO', descricao: '', parecer: '', status: 'EM_TRAMITACAO' }; // PLENARIUM:TRAMITAR_SEM_COMISSAO -- a comissao vai pelo "Encaminhar a comissao"

// >>> PLENARIUM:TRAMITE_GAVETA -- visual da lista e do painel lateral
const COR_ETAPA = { RECEBIMENTO: '#3b82f6', COMISSAO: '#f59e0b', PLENARIO: '#6366f1', PROMULGADO: '#16a34a', ARQUIVADO: '#64748b', DEVOLVIDO: '#dc2626' };
// O caminho da materia: Recebimento > Comissao > Plenario > desfecho
function CaminhoTramite({ etapa, status, grande = false }) {
  const finais = ['PROMULGADO', 'ARQUIVADO', 'DEVOLVIDO'];
  const fim = finais.includes(etapa) ? etapa : (finais.includes(status) ? status : null);
  const passos = [['RECEBIMENTO', 'Recebimento'], ['COMISSAO', 'Comissão'], ['PLENARIO', 'Plenário'], [fim || 'FIM', fim ? (ETAPA_LABEL[fim]?.label || fim) : 'Conclusão']];
  const atual = fim ? 3 : Math.max(0, passos.findIndex(x => x[0] === etapa));
  return (
    <div className={`ptr-caminho ${grande ? 'grande' : ''}`}>
      {passos.map(([k, rot], i) => {
        const feito = i < atual; const agora = i === atual;
        const cor = agora ? (COR_ETAPA[k] || '#6366f1') : feito ? '#94a3b8' : '#e2e8f0';
        return (
          <React.Fragment key={k + i}>
            {i > 0 && <span className="ptr-cam-traco" style={{ background: i <= atual ? '#cbd5e1' : '#eef2f7' }} />}
            <span className={`ptr-cam-passo ${agora ? 'agora' : ''}`} style={agora ? { color: cor } : feito ? { color: '#64748b' } : undefined}>
              <i style={{ background: cor, boxShadow: agora ? `0 0 0 3px ${cor}33` : 'none' }} />{rot}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}
const CSS_TRAMITE = `
.ptr-lista { overflow: visible; }
.ptr-item { position: relative; display: flex; align-items: stretch; gap: 12px; padding: 14px 18px; border-bottom: 1px solid var(--cinza, #e2e8f0); transition: background .15s; }
.ptr-item:last-child { border-bottom: 0; }
.ptr-item:hover { background: #f8fafc; }
.ptr-item.ativo { background: #eff6ff; box-shadow: inset 3px 0 0 #2563eb; }
.ptr-corpo { flex: 1; min-width: 0; text-align: left; background: none; border: 0; padding: 0; cursor: pointer; font: inherit; color: inherit; }
.ptr-linha1 { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
.ptr-num { font-size: .9rem; color: #0f172a; }
.ptr-tipo { font-size: .72rem; font-weight: 700; color: #475569; background: #f1f5f9; border-radius: 6px; padding: 2px 8px; }
.ptr-pauta { font-size: .7rem; font-weight: 700; color: #4338ca; background: #eef2ff; border-radius: 6px; padding: 2px 8px; }
.ptr-ementa { font-weight: 600; font-size: .9rem; color: #0f172a; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.ptr-autor { font-size: .76rem; color: var(--texto-suave, #64748b); margin-top: 3px; }
.ptr-caminho { display: flex; align-items: center; gap: 6px; margin-top: 9px; flex-wrap: wrap; }
.ptr-cam-passo { display: inline-flex; align-items: center; gap: 5px; font-size: .7rem; font-weight: 600; color: #94a3b8; white-space: nowrap; }
.ptr-cam-passo.agora { font-weight: 800; }
.ptr-cam-passo i { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.ptr-cam-traco { width: 18px; height: 2px; border-radius: 2px; }
.ptr-caminho.grande { gap: 8px; }
.ptr-caminho.grande .ptr-cam-passo { font-size: .82rem; }
.ptr-caminho.grande .ptr-cam-passo i { width: 11px; height: 11px; }
.ptr-caminho.grande .ptr-cam-traco { width: 28px; }
.ptr-lado { position: relative; display: flex; flex-direction: column; align-items: flex-end; justify-content: space-between; gap: 8px; flex-shrink: 0; }
.ptr-data { font-size: .7rem; color: #94a3b8; }
.ptr-icones { display: flex; align-items: center; gap: 4px; }
.ptr-ic { position: relative; width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #475569; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.ptr-ic:hover, .ptr-ic.on { border-color: #93c5fd; color: #1d4ed8; background: #eff6ff; }
.ptr-ic small { position: absolute; top: -6px; right: -6px; background: #1d4ed8; color: #fff; font-size: .6rem; font-weight: 800; border-radius: 999px; min-width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; padding: 0 4px; }
.ptr-ic.perigo:hover { border-color: #fca5a5; color: #dc2626; background: #fef2f2; }
.ptr-abrir { height: 32px; padding: 0 10px 0 12px; border-radius: 8px; border: 1px solid #2563eb; background: #2563eb; color: #fff; font: inherit; font-size: .78rem; font-weight: 700; display: inline-flex; align-items: center; gap: 2px; cursor: pointer; }
.ptr-abrir:hover { background: #1d4ed8; }
.ptr-fora { position: fixed; inset: 0; z-index: 40; }
.ptr-pop, .ptr-menu { position: absolute; top: calc(100% + 6px); right: 0; z-index: 41; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 12px 32px rgba(15,23,42,.16); }
.ptr-pop { width: 380px; max-width: 86vw; padding: 10px 12px; }
.ptr-pop-cab { display: flex; align-items: center; justify-content: space-between; font-size: .78rem; font-weight: 700; color: #64748b; padding-bottom: 8px; border-bottom: 1px solid #f1f5f9; }
.ptr-pop-cab button { border: 0; background: none; color: #94a3b8; cursor: pointer; }
.ptr-mov { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
.ptr-mov-data { width: 42px; text-align: right; line-height: 1.1; flex-shrink: 0; }
.ptr-mov-data b { display: block; font-size: .8rem; color: #0f172a; }
.ptr-mov-data small { font-size: .65rem; color: #94a3b8; }
.ptr-mov-ponto { width: 9px; height: 9px; border-radius: 50%; margin-top: 4px; flex-shrink: 0; }
.ptr-mov-txt { min-width: 0; }
.ptr-mov-txt b { display: block; font-size: .8rem; color: #0f172a; }
.ptr-mov-txt span { display: block; font-size: .75rem; color: #64748b; line-height: 1.4; }
.ptr-pop-tudo { margin-top: 8px; width: 100%; border: 0; background: #f8fafc; border-radius: 8px; padding: 7px; font: inherit; font-size: .76rem; font-weight: 700; color: #1d4ed8; display: flex; align-items: center; justify-content: center; gap: 2px; cursor: pointer; }
.ptr-menu { width: 210px; padding: 6px; }
.ptr-menu button { width: 100%; display: flex; align-items: center; gap: 9px; padding: 8px 10px; border: 0; background: none; border-radius: 8px; font: inherit; font-size: .8rem; font-weight: 600; color: #334155; cursor: pointer; text-align: left; }
.ptr-menu button:hover { background: #f1f5f9; }
.ptr-menu button.perigo { color: #dc2626; }
.ptr-menu button.perigo:hover { background: #fef2f2; }
.ptr-menu-sep { height: 1px; background: #f1f5f9; margin: 4px 0; }
.ptr-vazio { font-size: .8rem; color: #94a3b8; font-style: italic; padding: 6px 0; margin: 0; }
.ptr-gaveta-fundo { position: fixed; inset: 0; z-index: 950; background: rgba(15,23,42,.35); display: flex; justify-content: flex-end; animation: ptrFade .15s ease; }
.ptr-gaveta { width: min(760px, 100vw); height: 100%; background: #fff; display: flex; flex-direction: column; box-shadow: -12px 0 40px rgba(15,23,42,.18); animation: ptrEntra .2s ease; }
@keyframes ptrFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ptrEntra { from { transform: translateX(40px); opacity: .6; } to { transform: none; opacity: 1; } }
.ptr-g-cab { display: flex; align-items: flex-start; gap: 12px; padding: 20px 22px 12px; }
.ptr-g-tit { font-size: 1.12rem; font-weight: 800; color: #0f172a; line-height: 1.35; margin: 2px 0 4px; }
.ptr-g-fechar { width: 38px; height: 38px; border-radius: 10px; border: 1px solid #e2e8f0; background: #fff; color: #475569; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
.ptr-g-acoes { display: flex; gap: 6px; flex-wrap: wrap; padding: 0 22px 12px; }
.ptr-g-abas { display: flex; gap: 2px; padding: 0 16px; border-bottom: 1px solid #e2e8f0; overflow-x: auto; }
.ptr-g-abas button { border: 0; background: none; padding: 10px 12px; font: inherit; font-size: .8rem; font-weight: 700; color: #64748b; border-bottom: 2px solid transparent; cursor: pointer; white-space: nowrap; }
.ptr-g-abas button.on { color: #1d4ed8; border-bottom-color: #2563eb; }
.ptr-g-corpo { flex: 1; overflow-y: auto; padding: 18px 22px; }
.ptr-g-rodape { padding: 10px 22px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; }
.ptr-excluir { border: 0; background: none; color: #dc2626; font: inherit; font-size: .78rem; font-weight: 700; display: inline-flex; align-items: center; gap: 5px; cursor: pointer; }
.ptr-bloco { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 12px; padding: 12px 14px; margin-bottom: 12px; }
.ptr-rot { font-size: .66rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
.ptr-grade { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.ptr-grade > div { background: #fff; border: 1px solid #eef2f7; border-radius: 12px; padding: 10px 12px; }
.ptr-grade b { font-size: .85rem; color: #0f172a; }
.ptr-desc { font-size: .84rem; color: #334155; line-height: 1.6; margin: 0; white-space: pre-wrap; }
.ptr-linha-tempo { position: relative; padding-left: 4px; }
.ptr-lt-item { position: relative; display: flex; gap: 14px; padding-bottom: 16px; }
.ptr-lt-item:not(:last-child)::before { content: ''; position: absolute; left: 5px; top: 14px; bottom: 0; width: 2px; background: #e2e8f0; }
.ptr-lt-ponto { width: 12px; height: 12px; border-radius: 50%; margin-top: 3px; flex-shrink: 0; position: relative; z-index: 1; box-shadow: 0 0 0 3px #fff; }
.ptr-lt-txt { flex: 1; min-width: 0; }
.ptr-lt-topo { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.ptr-lt-topo b { font-size: .85rem; color: #0f172a; }
.ptr-lt-topo span { font-size: .72rem; color: #94a3b8; }
.ptr-lt-txt p { font-size: .8rem; color: #475569; margin: 2px 0 0; line-height: 1.5; }
.ptr-lt-obs { font-style: italic; }
.ptr-lt-txt small { font-size: .7rem; color: #94a3b8; }
.ptr-doc { display: flex; align-items: center; gap: 10px; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 12px; background: #fff; }
.ptr-doc-ic { width: 38px; height: 38px; border-radius: 10px; background: #eff6ff; color: #1d4ed8; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
@media (max-width: 720px) { .ptr-item { flex-direction: column; } .ptr-lado { flex-direction: row; align-items: center; justify-content: space-between; } .ptr-grade { grid-template-columns: 1fr; } }
`;
// <<< PLENARIUM:TRAMITE_GAVETA

// >>> PLENARIUM:AUTORIA -- quem apresentou a materia
const TIPOS_AUTORIA_UI = [['VEREADORES', 'Vereador(es)'], ['COMISSAO', 'Comissão'], ['MESA', 'Mesa Diretora'], ['EXECUTIVO', 'Poder Executivo'], ['POPULAR', 'Iniciativa popular'], ['OUTRO', 'Outro']];
const AUTORIA_VAZIA = { tipo: 'VEREADORES', vereadorIds: [], comissaoId: '', texto: '' };
const autoriaDe = (p) => (p && p.autoria ? { ...AUTORIA_VAZIA, ...p.autoria, comissaoId: p.autoria.comissaoId || '', texto: p.autoria.texto || '' }
  : p && p.vereadorId ? { ...AUTORIA_VAZIA, vereadorIds: [p.vereadorId] }
  : { ...AUTORIA_VAZIA, tipo: 'OUTRO', texto: (p && p.autor) || '' });
function textoAutoria(a, vereadores, comissoes) {
  const nome = (v) => v.nomeParlamentar || v.nome;
  const juntar = (l) => (l.length <= 1 ? (l[0] || '') : l.slice(0, -1).join(', ') + ' e ' + l[l.length - 1]);
  if (a.tipo === 'VEREADORES') { const ns = a.vereadorIds.map(id => vereadores.find(v => v.id === id)).filter(Boolean).map(nome); return ns.length ? (ns.length === 1 ? 'Ver. ' : 'Vereadores ') + juntar(ns) : ''; }
  if (a.tipo === 'COMISSAO') { const c = comissoes.find(x => x.id === a.comissaoId); return c ? c.nome : ''; }
  const t = (a.texto || '').trim();
  if (a.tipo === 'POPULAR') return t ? `Iniciativa Popular — ${t}` : 'Iniciativa Popular';
  return t || ({ MESA: 'Mesa Diretora', EXECUTIVO: 'Poder Executivo Municipal' }[a.tipo] || '');
}
function AutoriaProjeto({ valor, onChange, vereadores, comissoes }) {
  const a = valor || AUTORIA_VAZIA;
  const ativos = (vereadores || []).filter(v => v.ativo !== false);
  const marcar = (id) => onChange({ ...a, vereadorIds: a.vereadorIds.includes(id) ? a.vereadorIds.filter(x => x !== id) : [...a.vereadorIds, id] });
  const previa = textoAutoria(a, ativos, comissoes || []);
  return (
    <div className="form-group" style={{ gridColumn: '1/-1' }}>
      {a.tipo === 'VEREADORES' && (
        <>
          <label className="form-label">Vereadores autores * <span style={{ fontWeight: 400, color: 'var(--texto-suave)' }}>— marque um ou mais; o 1º marcado é o autor principal</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 6, maxHeight: 210, overflowY: 'auto', padding: 2 }}>
            {ativos.map(v => {
              const pos = a.vereadorIds.indexOf(v.id);
              const on = pos >= 0;
              return (
                <button key={v.id} type="button" onClick={() => marcar(v.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 9, border: `1.5px solid ${on ? '#2563eb' : 'var(--cinza, #e2e8f0)'}`, background: on ? '#eff6ff' : '#fff', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 800, background: on ? '#2563eb' : '#f1f5f9', color: on ? '#fff' : '#94a3b8' }}>{on ? pos + 1 : ''}</span>
                  <span style={{ minWidth: 0 }}><b style={{ display: 'block', fontSize: '.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#0f172a' }}>{v.nomeParlamentar || v.nome}</b><small style={{ fontSize: '.68rem', color: 'var(--texto-suave)' }}>{v.partido || ''}</small></span>
                </button>
              );
            })}
          </div>
        </>
      )}
      {a.tipo === 'COMISSAO' && (
        <>
          <label className="form-label">Comissão autora *</label>
          <select className="form-input form-select" value={a.comissaoId} onChange={e => onChange({ ...a, comissaoId: e.target.value })}>
            <option value="">Selecione a comissão</option>
            {(comissoes || []).filter(c => c.ativa !== false).map(c => <option key={c.id} value={c.id}>{c.nome}{c.sigla ? ` (${c.sigla})` : ''}</option>)}
          </select>
          {!(comissoes || []).length && <small style={{ color: 'var(--texto-suave)' }}>Nenhuma comissão cadastrada. Cadastre em Comissões.</small>}
        </>
      )}
      {['MESA', 'EXECUTIVO', 'POPULAR', 'OUTRO'].includes(a.tipo) && (
        <>
          <label className="form-label">{a.tipo === 'OUTRO' ? 'Nome do autor *' : a.tipo === 'POPULAR' ? 'Responsável ou entidade (opcional)' : 'Como deve aparecer (opcional)'}</label>
          <input className="form-input" value={a.texto} maxLength={200} onChange={e => onChange({ ...a, texto: e.target.value })}
            placeholder={a.tipo === 'MESA' ? 'Mesa Diretora' : a.tipo === 'EXECUTIVO' ? 'Poder Executivo Municipal' : a.tipo === 'POPULAR' ? 'Ex.: Associação de Moradores do Bairro X' : 'Nome do autor'} />
        </>
      )}
      <div style={{ marginTop: 8, fontSize: '.78rem', color: previa ? '#1d4ed8' : 'var(--texto-suave)', background: previa ? '#eff6ff' : 'transparent', borderRadius: 8, padding: previa ? '6px 10px' : 0 }}>
        {previa ? <>Autor: <b>{previa}</b></> : 'Escolha o autor da matéria.'}
      </div>
    </div>
  );
}
// <<< PLENARIUM:AUTORIA

export default function Projetos() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vereadores, setVereadores] = useState([]);
  const [pautas, setPautas] = useState([]);
  const [modal, setModal] = useState(false);
  // Guarda o id quando o modal está editando; vazio quando é cadastro novo.
  const [editandoId, setEditandoId] = useState(null);
  const [modalTramitar, setModalTramitar] = useState(false);
  const [modalVincular, setModalVincular] = useState(false);
  const [projetoSelecionado, setProjetoSelecionado] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [formTramitar, setFormTramitar] = useState(VAZIO_TRAMITE);
  const [pautaVincular, setPautaVincular] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const buscaRef = React.useRef(''); buscaRef.current = busca; // PLENARIUM:BUSCA_PROJETOS texto atual da busca
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  // Anexos carregados sob demanda, por materia: { projetoId: [...] }.
  // So sao buscados quando a materia e expandida -- a lista principal
  // nunca traz documento nenhum junto.
  const [anexos, setAnexos] = useState({});
  const [carregandoAnexos, setCarregandoAnexos] = useState(null);
  const [modalAnexo, setModalAnexo] = useState(null);
  const [formAnexo, setFormAnexo] = useState(VAZIO_ANEXO);
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  // PLENARIUM:TRAMITE_GAVETA -- painel lateral (aba) e os menus discretos da lista
  const [abaMateria, setAbaMateria] = useState('resumo');
  const [movAberto, setMovAberto] = useState(null);
  const [menuAberto, setMenuAberto] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroTipo)  params.tipo  = filtroTipo;
      if (filtroEtapa) params.etapa = filtroEtapa;
      if (buscaRef.current.trim()) params.busca = buscaRef.current.trim(); // PLENARIUM:BUSCA_PROJETOS
      const [pRes, vRes] = await Promise.all([
        axios.get('/api/projetos', { params }),
        axios.get('/api/vereadores'),
      ]);
      setLista(pRes.data);
      setVereadores(vRes.data);
    } catch { toast.error('Erro ao carregar projetos'); }
    finally { setLoading(false); }
  }, [filtroTipo, filtroEtapa]);

  const carregarPautas = async () => {
    try {
      const r = await axios.get('/api/sessoes');
      const todasPautas = r.data.flatMap(s => s.pautas?.map(p => ({ ...p, sessaoTitulo: s.titulo })) || []);
      setPautas(todasPautas.filter(p => !p.projetoId));
    } catch {}
  };

  useEffect(() => { carregar(); }, [filtroTipo, filtroEtapa]);

  // >>> PLENARIUM:TRAMITE_ABAS -- abas por situacao (sobre a lista ja filtrada)
  const [abaSituacao, setAbaSituacaoEstado] = useState(() => { try { return sessionStorage.getItem('ptr-aba') || 'todas'; } catch (e) { return 'todas'; } });
  const setAbaSituacao = (a) => { setAbaSituacaoEstado(a); try { sessionStorage.setItem('ptr-aba', a); } catch (e) { /* segue */ } };
  const situacaoDe = (p) => {
    if (['PROMULGADO', 'ARQUIVADO', 'DEVOLVIDO'].includes(p.etapaAtual) || (p.status && p.status !== 'EM_TRAMITACAO')) return 'concluidas';
    if (!p.etapaAtual || p.etapaAtual === 'RECEBIMENTO') return 'protocoladas';
    return 'tramitacao';
  };
  const contagemAbas = lista.reduce((c, p) => { c[situacaoDe(p)]++; return c; }, { protocoladas: 0, tramitacao: 0, concluidas: 0 });
  const listaAba = abaSituacao === 'todas' ? lista : lista.filter(p => situacaoDe(p) === abaSituacao);
  // <<< PLENARIUM:TRAMITE_ABAS

  // Abre o modal já preenchido para corrigir o cadastro — inclusive para
  // incluir o anexo, que antes só podia ir no momento do cadastro.
  const abrirEdicaoProjeto = (p) => {
    setEditandoId(p.id);
    setForm({
      numero: p.numero || '',
      ano: p.ano || new Date().getFullYear(),
      tipo: p.tipo || 'PROJETO_LEI',
      ementa: p.ementa || '',
      descricao: p.descricao || '',
      autor: p.autor || '',
      vereadorId: p.vereadorId || '',
      arquivoUrl: '',
      autoria: autoriaDe(p) // PLENARIUM:AUTORIA
    });
    setModal(true);
  };
  // PLENARIUM:AUTORIA -- comissoes para a autoria (so quando o cadastro abre)
  const [comissoesAut, setComissoesAut] = useState([]);
  useEffect(() => { if (modal) axios.get('/api/comissoes').then(r => setComissoesAut(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [modal]);
  const salvar = async (e) => {
    e.preventDefault();
    if (form.tipo === 'ATA_ANTERIOR') form.autoria = { ...AUTORIA_VAZIA, tipo: 'OUTRO', texto: 'Poder Legislativo' }; // PLENARIUM:AJUSTES_2110
    { const au = form.autoria; const txt = au ? textoAutoria(au, vereadores, comissoesAut) : form.autor; if (!txt) { toast.error(au && au.tipo === 'VEREADORES' ? 'Marque pelo menos um vereador autor.' : au && au.tipo === 'COMISSAO' ? 'Escolha a comissão autora.' : 'Informe o autor.'); return; } form.autor = txt; } // PLENARIUM:AUTORIA
    setSalvando(true);
    // O arquivo NAO vai mais junto do cadastro. A materia e salva
    // primeiro e o documento vai depois, como anexo -- assim tudo
    // fica num lugar so e o campo antigo (arquivoUrl) para de crescer.
    const { arquivoUrl, ...dados } = form;
    try {
      let projetoId = editandoId;
      if (editandoId) {
        await axios.put(`/api/projetos/${editandoId}`, dados);
        toast.success('Projeto atualizado!');
      } else {
        const r = await axios.post('/api/projetos', dados);
        projetoId = r.data && r.data.id;
        toast.success('Projeto registrado!');
      }
      setModal(false); setForm(VAZIO); setEditandoId(null);

      // Segunda parte: o anexo. A materia JA esta salva neste ponto,
      // entao uma falha aqui nao pode passar por falha do cadastro --
      // mas tambem nao pode ficar calada, senao o documento some sem
      // ninguem perceber.
      if (arquivoUrl) {
        if (!projetoId) {
          toast.error('A matéria foi salva, mas o documento não foi anexado. Abra a matéria e use "Anexar documento".', { duration: 9000 });
        } else {
          try {
            await axios.post(`/api/projetos/${projetoId}/anexos`, {
              nome: 'Documento principal',
              tipo: 'PROJETO',
              arquivo: arquivoUrl
            });
            setAnexos(a => { const copia = { ...a }; delete copia[projetoId]; return copia; });
          } catch (e2) {
            toast.error('A matéria foi salva, mas o documento NÃO subiu. Abra a matéria e use "Anexar documento".', { duration: 9000 });
          }
        }
      }
      carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const tramitar = async (e) => {
    e.preventDefault(); setSalvando(true);
    try {
      await axios.post(`/api/projetos/${projetoSelecionado.id}/tramitar`, formTramitar);
      toast.success('Projeto tramitado!');
      setModalTramitar(false); setFormTramitar(VAZIO_TRAMITE); carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao tramitar'); }
    finally { setSalvando(false); }
  };

  const vincularPauta = async (e) => {
    e.preventDefault();
    if (!pautaVincular) { toast.error('Selecione uma pauta!'); return; }
    setSalvando(true);
    try {
      await axios.patch(`/api/projetos/${projetoSelecionado.id}/vincular-pauta`, { pautaId: pautaVincular });
      toast.success('Projeto vinculado à pauta!');
      setModalVincular(false); setPautaVincular(''); carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao vincular'); }
    finally { setSalvando(false); }
  };

  const excluir = async (id, numero) => {
    if (!window.confirm(`Remover a matéria ${numero}?\n\nUma cópia fica na lixeira do suporte. Se ela já tramitou, a exclusão vira um pedido ao suporte.`)) return;
    // PLENARIUM:LIXEIRA_MATERIAS -- ja tramitada: abre o pedido de exclusao (motivo) em vez de apagar
    try { if (await excluirComConfirmacao(`/api/projetos/${id}`)) { toast.success('Matéria removida (cópia na lixeira).'); carregar(); } }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao remover'); }
  };

  const handleBusca = (e) => { e.preventDefault(); carregar(); };


const abrirArquivo = (url) => {
  if (!url) return;
  if (url.startsWith('data:')) {
    const arr = url.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const blob = new Blob([u8arr], { type: mime });
    window.open(URL.createObjectURL(blob), '_blank');
  } else {
    window.open(url, '_blank');
  }
};

  // Tamanho vem como comprimento do texto base64; os bytes reais
  // sao cerca de tres quartos disso.
  const tamanhoLegivel = (n) => {
    const bytes = Math.round((n || 0) * 0.75);
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
    return bytes + ' B';
  };

  const carregarAnexos = async (projetoId) => {
    setCarregandoAnexos(projetoId);
    try {
      const r = await axios.get(`/api/projetos/${projetoId}/anexos`);
      setAnexos(a => ({ ...a, [projetoId]: r.data }));
    } catch { toast.error('Erro ao carregar os documentos'); }
    finally { setCarregandoAnexos(null); }
  };

  // Só busca os anexos ao ABRIR, e só na primeira vez de cada matéria.
  const alternarExpandido = (p) => {
    const novo = expandido === p.id ? null : p.id;
    setExpandido(novo);
    if (novo && !anexos[novo]) carregarAnexos(novo);
  };
  // PLENARIUM:TRAMITE_GAVETA -- abre a materia no painel lateral, na aba pedida
  const abrirMateria = (p, aba) => { setMovAberto(null); setMenuAberto(null); setAbaMateria(aba || 'resumo'); setExpandido(p.id); if (!anexos[p.id]) carregarAnexos(p.id); };
  const fecharMateria = () => setExpandido(null);
  const materiaAberta = expandido ? lista.find(x => x.id === expandido) : null;
  useEffect(() => {
    if (!expandido) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !modal && !modalTramitar && !modalVincular && !modalAnexo) setExpandido(null); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [expandido, modal, modalTramitar, modalVincular, modalAnexo]);

  // O PDF só sai do banco aqui, no clique. Reaproveita o abrirArquivo
  // que ja existia, em vez de ter dois jeitos de abrir documento.
  const abrirAnexo = async (anexoId) => {
    try {
      const r = await axios.get(`/api/projetos/anexos/${anexoId}`);
      abrirArquivo(r.data.arquivo);
    } catch { toast.error('Erro ao abrir o documento'); }
  };

  const excluirAnexo = async (anexo, projetoId) => {
    if (!window.confirm(`Remover o documento "${anexo.nome}"?`)) return;
    try {
      await axios.delete(`/api/projetos/anexos/${anexo.id}`);
      toast.success('Documento removido');
      carregarAnexos(projetoId);
    } catch { toast.error('Erro ao remover o documento'); }
  };

  const escolherArquivoAnexo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > LIMITE_ANEXO_MB * 1024 * 1024) {
      toast.error(`O arquivo tem ${(file.size / (1024 * 1024)).toFixed(1)}MB. O limite é ${LIMITE_ANEXO_MB}MB.`);
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFormAnexo(f => ({
      ...f,
      arquivo: reader.result,
      nomeArquivo: file.name,
      // Sugere o nome do arquivo, sem a extensao, se o campo estiver vazio.
      nome: f.nome || file.name.replace(/\.[^.]+$/, '')
    }));
    reader.readAsDataURL(file);
  };

  const enviarAnexo = async (e) => {
    e.preventDefault();
    if (!formAnexo.arquivo) { toast.error('Escolha um arquivo.'); return; }
    const projetoId = modalAnexo.id;
    setEnviandoAnexo(true);
    try {
      await axios.post(`/api/projetos/${projetoId}/anexos`, formAnexo);
      toast.success('Documento anexado!');
      setModalAnexo(null); setFormAnexo(VAZIO_ANEXO);
      carregarAnexos(projetoId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao anexar o documento');
    }
    finally { setEnviandoAnexo(false); }
  };

  return (
    <div>
      <div className="ptr-fixo"> {/* PLENARIUM:TRAMITE_FIXO -- titulo, filtros e abas ficam fixos ao rolar */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Trâmite Legislativo</h1>
          <p className="page-subtitle">{lista.length} projeto(s) encontrado(s)</p>
          <div style={{ marginTop: 8 }}><BotaoMateriasTeste onFeito={carregar} /></div>{/* PLENARIUM:HOMOLOGACAO */}
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(VAZIO); setEditandoId(null); setModal(true); }}>
          <Plus size={15} /> Novo Projeto
        </button>
      </div>

      {/* Filtros */}
      <div className="toolbar">
        <form onSubmit={handleBusca} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
          <input
            className="form-input"
            placeholder="Buscar por número, ementa ou autor..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn btn-secondary" type="submit"><Search size={14} /></button>
        </form>
        <select className="form-input form-select" style={{ width: 160 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-input form-select" style={{ width: 160 }} value={filtroEtapa} onChange={e => setFiltroEtapa(e.target.value)}>
          <option value="">Todas as etapas</option>
          {Object.entries(ETAPA_LABEL).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
      </div>

      {/* PLENARIUM:TRAMITE_ABAS -- situacao da materia */}
      <style>{`
.pab { display: flex; gap: 4px; border-bottom: 1px solid var(--cinza, #e2e8f0); margin: 4px 0 14px; overflow-x: auto; scrollbar-width: none; }
.pab::-webkit-scrollbar { display: none; }
.pab-aba { position: relative; display: inline-flex; align-items: center; gap: 8px; padding: 10px 14px 12px; border: 0; background: none; font: inherit; font-size: .88rem; font-weight: 600; color: var(--texto-suave, #64748b); cursor: pointer; white-space: nowrap; border-radius: 8px 8px 0 0; }
.pab-aba:hover { color: var(--texto, #0f172a); background: #f8fafc; }
.pab-aba.on { color: #1d4ed8; }
.pab-aba.on::after { content: ''; position: absolute; left: 10px; right: 10px; bottom: -1px; height: 3px; border-radius: 3px 3px 0 0; background: #1d4ed8; }
.pab-n { min-width: 22px; padding: 1px 8px; border-radius: 999px; font-size: .74rem; font-weight: 800; background: #f1f5f9; color: #475569; text-align: center; }
.pab-aba.on .pab-n { background: #dbeafe; color: #1d4ed8; }
.pab-ponto { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.pab-dica { font-size: .78rem; color: var(--texto-suave, #64748b); margin: -6px 0 12px 2px; }
.ptr-fixo { position: sticky; top: var(--header-altura, 56px); z-index: 30; background: var(--fundo, #f1f4f9); margin: -28px -28px 0; padding: 22px 28px 1px; }
.ptr-fixo .page-header { margin-bottom: 14px; }
@media (max-width: 900px) { .ptr-fixo { position: static; margin: 0; padding: 0; background: none; } }
`}</style>
      <div className="pab" role="tablist">
        {[
          ['todas', 'Todas', lista.length, '#94a3b8'],
          ['protocoladas', 'Protocoladas', contagemAbas.protocoladas, '#3b82f6'],
          ['tramitacao', 'Em tramitação', contagemAbas.tramitacao, '#f59e0b'],
          ['concluidas', 'Concluídas', contagemAbas.concluidas, '#22c55e'],
        ].map(([k, rot, n, cor]) => (
          <button key={k} type="button" role="tab" aria-selected={abaSituacao === k} className={`pab-aba ${abaSituacao === k ? 'on' : ''}`} onClick={() => setAbaSituacao(k)}>
            {k !== 'todas' && <span className="pab-ponto" style={{ background: cor }} />}{rot}<span className="pab-n">{n}</span>
          </button>
        ))}
      </div>
      {abaSituacao !== 'todas' && (
        <p className="pab-dica">{abaSituacao === 'protocoladas' ? 'Recebidas e aguardando o primeiro despacho (encaminhar à comissão ou ao Plenário).' : abaSituacao === 'tramitacao' ? 'Nas comissões ou no Plenário, ainda sem decisão final.' : 'Com decisão final: aprovadas, rejeitadas, promulgadas, arquivadas ou devolvidas.'}</p>
      )}
      </div> {/* PLENARIUM:TRAMITE_FIXO fim */}

      {/* >>> PLENARIUM:TRAMITE_GAVETA -- lista limpa + painel lateral da materia.
          Cada materia e uma linha fechada: numero, tipo, situacao, ementa,
          autor e o caminho da tramitacao. Os detalhes abrem num painel ao
          lado (nao empurram a materia de baixo). Icones discretos:
          movimentacoes (resumo rapido), documentos e o menu de acoes. */}
      <style>{CSS_TRAMITE}</style>
      <div className="card ptr-lista" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>
        ) : listaAba.length === 0 ? (
          <div className="sem-dados"><p>{lista.length === 0 ? 'Nenhum projeto encontrado.' : 'Nenhuma matéria nesta aba.'}</p></div>
        ) : listaAba.map(p => {
          const fechado = ['PROMULGADO', 'ARQUIVADO', 'DEVOLVIDO'].includes(p.status);
          const st = STATUS_LABEL[p.status] || { label: p.status, cor: 'badge-cinza' };
          const movs = [...(p.tramitacoes || [])].sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));
          const ultima = movs[0];
          return (
            <div key={p.id} className={`ptr-item ${expandido === p.id ? 'ativo' : ''}`}>
              <button type="button" className="ptr-corpo" onClick={() => abrirMateria(p, 'resumo')} title="Abrir a matéria">
                <div className="ptr-linha1">
                  <strong className="font-mono ptr-num">{p.numero}</strong>
                  <span className="ptr-tipo">{TIPO_LABEL[p.tipo] || p.tipo}</span>
                  {p.pautas?.length > 0 && <span className="ptr-pauta">Na pauta</span>}
                </div>
                <div className="ptr-ementa">{p.ementa}</div>
                <div className="ptr-autor">{p.autor}{p.vereador ? ` · ${p.vereador.nome}${p.vereador.partido ? ` (${p.vereador.partido})` : ''}` : ''}</div>
                <CaminhoTramite etapa={p.etapaAtual} status={p.status} />
              </button>
              <div className="ptr-lado">
                <span className={`badge ${st.cor}`}>{st.label}</span>
                {ultima && <span className="ptr-data" title="Última movimentação">{new Date(ultima.criadoEm).toLocaleDateString('pt-BR')}</span>}
                <div className="ptr-icones">
                  <button type="button" className={`ptr-ic ${movAberto === p.id ? 'on' : ''}`} title="Últimas movimentações" onClick={(e) => { e.stopPropagation(); setMenuAberto(null); setMovAberto(movAberto === p.id ? null : p.id); }}><History size={15} /></button>
                  <button type="button" className="ptr-ic" title="Documentos da matéria" onClick={() => abrirMateria(p, 'documentos')}><Paperclip size={15} />{anexos[p.id] && anexos[p.id].length > 0 ? <small>{anexos[p.id].length}</small> : null}</button>
                  <button type="button" className={`ptr-ic ${menuAberto === p.id ? 'on' : ''}`} title="Ações" onClick={(e) => { e.stopPropagation(); setMovAberto(null); setMenuAberto(menuAberto === p.id ? null : p.id); }}><MoreHorizontal size={16} /></button>
                  <button type="button" className="ptr-abrir" onClick={() => abrirMateria(p, 'resumo')}>Abrir <ChevronRight size={14} /></button>
                </div>

                {movAberto === p.id && (
                  <>
                    <div className="ptr-fora" onClick={() => setMovAberto(null)} />
                    <div className="ptr-pop">
                      <div className="ptr-pop-cab"><span>Últimas movimentações</span><button type="button" onClick={() => setMovAberto(null)}><X size={14} /></button></div>
                      {!movs.length ? <p className="ptr-vazio">Nenhuma movimentação.</p> : movs.slice(0, 4).map(t => (
                        <div key={t.id} className="ptr-mov">
                          <div className="ptr-mov-data"><b>{new Date(t.criadoEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</b><small>{new Date(t.criadoEm).getFullYear()}</small></div>
                          <span className="ptr-mov-ponto" style={{ background: COR_ETAPA[t.etapa] || '#94a3b8' }} />
                          <div className="ptr-mov-txt"><b>{ETAPA_LABEL[t.etapa]?.label || t.etapa}</b><span>{t.descricao}</span></div>
                        </div>
                      ))}
                      <button type="button" className="ptr-pop-tudo" onClick={() => { setMovAberto(null); abrirMateria(p, 'tramitacao'); }}>Ver toda a tramitação <ChevronRight size={13} /></button>
                    </div>
                  </>
                )}

                {menuAberto === p.id && (
                  <>
                    <div className="ptr-fora" onClick={() => setMenuAberto(null)} />
                    <div className="ptr-menu">
                      {!fechado && <button type="button" onClick={() => { setMenuAberto(null); abrirEdicaoProjeto(p); }}><Pencil size={14} /> Editar cadastro</button>}
                      {!fechado && <button type="button" onClick={() => { setMenuAberto(null); setProjetoSelecionado(p); setFormTramitar(VAZIO_TRAMITE); setModalTramitar(true); }}><ArrowRightLeft size={14} /> Tramitar</button>}
                      {!fechado && <button type="button" onClick={() => { setMenuAberto(null); setProjetoSelecionado(p); carregarPautas(); setModalVincular(true); }}><Link size={14} /> Vincular à pauta</button>}
                      <button type="button" onClick={() => { setMenuAberto(null); setModalAnexo(p); setFormAnexo(VAZIO_ANEXO); }}><Plus size={14} /> Anexar documento</button>
                      <div className="ptr-menu-sep" />
                      <button type="button" className="perigo" onClick={() => { setMenuAberto(null); excluir(p.id, p.numero); }}><Trash2 size={14} /> Excluir matéria</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Painel lateral da materia */}
      {materiaAberta && (() => {
        const p = materiaAberta;
        const fechado = ['PROMULGADO', 'ARQUIVADO', 'DEVOLVIDO'].includes(p.status);
        const st = STATUS_LABEL[p.status] || { label: p.status, cor: 'badge-cinza' };
        const movs = [...(p.tramitacoes || [])].sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));
        const docs = anexos[p.id];
        const abas = [['resumo', 'Resumo'], ['tramitacao', `Tramitação${movs.length ? ` (${movs.length})` : ''}`], ['comissoes', 'Comissões e pareceres'], ['documentos', `Documentos${docs && docs.length ? ` (${docs.length})` : ''}`]];
        if (p.tipo === 'ATA_ANTERIOR') abas.push(['retificacoes', 'Retificações']);
        return (
          <div className="ptr-gaveta-fundo" onClick={fecharMateria}>
            <aside className="ptr-gaveta" onClick={e => e.stopPropagation()}>
              <div className="ptr-g-cab">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="ptr-linha1">
                    <strong className="font-mono ptr-num">{p.numero}</strong>
                    <span className="ptr-tipo">{TIPO_LABEL[p.tipo] || p.tipo}</span>
                    <span className={`badge ${st.cor}`}>{st.label}</span>
                  </div>
                  <h2 className="ptr-g-tit">{p.ementa}</h2>
                  <div className="ptr-autor">{p.autor}{p.vereador ? ` · ${p.vereador.nome}${p.vereador.partido ? ` (${p.vereador.partido})` : ''}` : ''}</div>
                </div>
                <button type="button" className="ptr-g-fechar" onClick={fecharMateria} title="Fechar"><X size={20} /></button>
              </div>
              <div className="ptr-g-acoes">
                {!fechado && <button type="button" className="btn btn-secondary btn-sm" onClick={() => abrirEdicaoProjeto(p)}><Pencil size={13} /> Editar</button>}
                {!fechado && <button type="button" className="btn btn-primary btn-sm" onClick={() => { setProjetoSelecionado(p); setFormTramitar(VAZIO_TRAMITE); setModalTramitar(true); }}><ArrowRightLeft size={13} /> Tramitar</button>}
                {!fechado && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setProjetoSelecionado(p); carregarPautas(); setModalVincular(true); }}><Link size={13} /> Vincular à pauta</button>}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setModalAnexo(p); setFormAnexo(VAZIO_ANEXO); }}><Plus size={13} /> Anexar documento</button>
              </div>
              <div className="ptr-g-abas">
                {abas.map(([id, rot]) => <button key={id} type="button" className={abaMateria === id ? 'on' : ''} onClick={() => setAbaMateria(id)}>{rot}</button>)}
              </div>
              <div className="ptr-g-corpo">
                {abaMateria === 'resumo' && (
                  <>
                    <div className="ptr-bloco">
                      <div className="ptr-rot">Caminho da matéria</div>
                      <CaminhoTramite etapa={p.etapaAtual} status={p.status} grande />
                    </div>
                    <div className="ptr-grade">
                      <div><div className="ptr-rot">Tipo</div><b>{TIPO_LABEL[p.tipo] || p.tipo}</b></div>
                      <div><div className="ptr-rot">Etapa atual</div><b>{ETAPA_LABEL[p.etapaAtual]?.label || p.etapaAtual}</b></div>
                      <div><div className="ptr-rot">Autor</div><b>{p.autor}</b></div>
                      <div><div className="ptr-rot">Pauta</div><b>{p.pautas?.length ? p.pautas.map(x => x.numero).join(', ') : '—'}</b></div>
                    </div>
                    {p.descricao && <div className="ptr-bloco"><div className="ptr-rot">Descrição completa</div><p className="ptr-desc">{p.descricao}</p></div>}
                    {movs[0] && (
                      <div className="ptr-bloco">
                        <div className="ptr-rot">Última movimentação</div>
                        <div className="ptr-mov" style={{ padding: 0, border: 0 }}>
                          <span className="ptr-mov-ponto" style={{ background: COR_ETAPA[movs[0].etapa] || '#94a3b8' }} />
                          <div className="ptr-mov-txt"><b>{ETAPA_LABEL[movs[0].etapa]?.label || movs[0].etapa} · {new Date(movs[0].criadoEm).toLocaleString('pt-BR')}</b><span>{movs[0].descricao}</span></div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {abaMateria === 'tramitacao' && (
                  !movs.length ? <p className="ptr-vazio">Nenhuma movimentação registrada.</p> : (
                    <div className="ptr-linha-tempo">
                      {movs.map(t => (
                        <div key={t.id} className="ptr-lt-item">
                          <span className="ptr-lt-ponto" style={{ background: COR_ETAPA[t.etapa] || '#94a3b8' }} />
                          <div className="ptr-lt-txt">
                            <div className="ptr-lt-topo"><b>{ETAPA_LABEL[t.etapa]?.label || t.etapa}</b><span>{new Date(t.criadoEm).toLocaleString('pt-BR')}</span></div>
                            <p>{t.descricao}</p>
                            {t.parecer && <p className="ptr-lt-obs">Observação: {t.parecer}</p>}
                            <small>{t.usuarioNome}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {abaMateria === 'comissoes' && (
                  <PareceresMateria projeto={p} onMudou={async () => {
                    carregarAnexos(p.id);
                    try { const r = await axios.get(`/api/projetos/${p.id}`); setLista(l => l.map(x => (x.id === p.id ? { ...x, ...r.data } : x))); } catch (e) { /* segue */ }
                  }} />
                )}
                {abaMateria === 'documentos' && (
                  carregandoAnexos === p.id ? <p className="ptr-vazio">Carregando documentos...</p>
                    : !docs || !docs.length ? <p className="ptr-vazio">Nenhum documento anexado.</p>
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {docs.map(a => (
                          <div key={a.id} className="ptr-doc">
                            <span className="ptr-doc-ic"><FileText size={18} /></span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <b style={{ fontSize: '0.85rem' }}>{a.nome}</b>
                                <span className="badge badge-cinza">{TIPO_ANEXO_LABEL[a.tipo] || a.tipo}</span>
                              </div>
                              <small style={{ color: 'var(--texto-suave)' }}>{tamanhoLegivel(a.tamanho)} · {a.usuarioNome} · {new Date(a.criadoEm).toLocaleDateString('pt-BR')}{a.descricao ? ` · ${a.descricao}` : ''}</small>
                            </div>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => abrirAnexo(a.id)}><FileText size={13} /> Abrir</button>
                            <button type="button" className="ptr-ic perigo" title="Remover o documento" onClick={() => excluirAnexo(a, p.id)}><Trash2 size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )
                )}
                {abaMateria === 'retificacoes' && p.tipo === 'ATA_ANTERIOR' && <RetificacoesAta projetoId={p.id} />}
              </div>
              <div className="ptr-g-rodape">
                <button type="button" className="ptr-excluir" onClick={() => { const n = p.numero; fecharMateria(); excluir(p.id, n); }}><Trash2 size={13} /> Excluir matéria</button>
              </div>
            </aside>
          </div>
        );
      })()}
      {/* <<< PLENARIUM:TRAMITE_GAVETA */}

      {/* Modal Novo Projeto */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2 className="modal-title"><FileText size={16} /> {editandoId ? 'Editar Projeto' : 'Novo Projeto Legislativo'}</h2>
              <button className="btn-modal-close" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={salvar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select className="form-input form-select" value={form.tipo} onChange={e => { const tipo = e.target.value; const eraAta = form.tipo === 'ATA_ANTERIOR'; setForm({ ...form, tipo, ...(tipo === 'ATA_ANTERIOR' ? { autoria: { ...AUTORIA_VAZIA, tipo: 'OUTRO', texto: 'Poder Legislativo' } } : eraAta ? { autoria: { ...AUTORIA_VAZIA } } : {}) }); }} required>{/* PLENARIUM:AJUSTES_2110 -- ata: autoria automatica */}
                    {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Autoria *</label>
                  {form.tipo === 'ATA_ANTERIOR' ? <div className="form-input" style={{ background: '#f1f5f9', color: '#334155', fontWeight: 600, cursor: 'default' }}>Poder Legislativo</div> : /* PLENARIUM:AJUSTES_2110 */
                  <select className="form-input form-select" value={(form.autoria || {}).tipo || 'VEREADORES'} onChange={e => setForm({ ...form, autoria: { ...(form.autoria || {}), tipo: e.target.value, vereadorIds: (form.autoria || {}).vereadorIds || [], comissaoId: (form.autoria || {}).comissaoId || '', texto: '' } })}>
                    {TIPOS_AUTORIA_UI.map(([k, r]) => <option key={k} value={k}>{r}</option>)}
                  </select>}
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Ementa *</label>
                  <input className="form-input" value={form.ementa} onChange={e => setForm({ ...form, ementa: e.target.value })} placeholder="Descrição resumida do projeto" required />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Descrição completa</label>
                  <textarea className="form-input" rows={3} value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} style={{ resize: 'vertical' }} />
                </div>
                {/* PLENARIUM:AUTORIA */}
                {form.tipo !== 'ATA_ANTERIOR' && <AutoriaProjeto valor={form.autoria} onChange={(au) => setForm({ ...form, autoria: au })} vereadores={vereadores} comissoes={comissoesAut} />}{/* PLENARIUM:AJUSTES_2110 */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Documento principal (PDF, máx. 5MB) — opcional</label>
                  <input type="file" accept=".pdf,image/*" className="form-input"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo deve ter no máximo 5MB!'); return; }
                      const reader = new FileReader();
                      reader.onload = () => setForm({ ...form, arquivoUrl: reader.result });
                      reader.readAsDataURL(file);
                    }}
                  />
                  {form.arquivoUrl && <p style={{ fontSize: '0.72rem', color: 'var(--aprovado)', marginTop: 3 }}>✅ Arquivo anexado</p>}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : (editandoId ? 'Salvar alterações' : 'Registrar Projeto')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Anexar Documento */}
      {modalAnexo && (
        <div className="modal-overlay" onClick={() => setModalAnexo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2 className="modal-title"><Paperclip size={16} /> Anexar documento — {modalAnexo.numero}</h2>
              <button className="btn-modal-close" onClick={() => setModalAnexo(null)}><X size={18} /></button>
            </div>
            <form onSubmit={enviarAnexo}>
              <div className="form-group">
                <label className="form-label">Tipo do documento *</label>
                <select className="form-input form-select" value={formAnexo.tipo} onChange={e => setFormAnexo({ ...formAnexo, tipo: e.target.value })} required>
                  {Object.entries(TIPO_ANEXO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Etapa a que se refere (opcional)</label>
                <select className="form-input form-select" value={formAnexo.etapa} onChange={e => setFormAnexo({ ...formAnexo, etapa: e.target.value })}>
                  <option value="">Não especificar</option>
                  {Object.entries(ETAPA_LABEL).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <p style={{ fontSize: '0.7rem', color: 'var(--texto-suave)', marginTop: 4 }}>
                  Serve apenas para organizar. Não impede anexar documento de uma etapa já vencida.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Nome do documento *</label>
                <input className="form-input" value={formAnexo.nome} onChange={e => setFormAnexo({ ...formAnexo, nome: e.target.value })} placeholder="Ex.: Parecer da Comissão de Justiça" required />
              </div>
              <div className="form-group">
                <label className="form-label">Observação (opcional)</label>
                <input className="form-input" value={formAnexo.descricao} onChange={e => setFormAnexo({ ...formAnexo, descricao: e.target.value })} placeholder="Ex.: favorável, com ressalvas" />
              </div>
              <div className="form-group">
                <label className="form-label">Arquivo * (PDF ou imagem, máx. {LIMITE_ANEXO_MB}MB)</label>
                <input type="file" accept=".pdf,image/*" className="form-input" onChange={escolherArquivoAnexo} />
                {formAnexo.arquivo && (
                  <p style={{ fontSize: '0.72rem', color: 'var(--aprovado)', marginTop: 3 }}>
                    ✅ {formAnexo.nomeArquivo || 'Arquivo pronto para envio'}
                  </p>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalAnexo(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={enviandoAnexo}>{enviandoAnexo ? 'Enviando...' : 'Anexar documento'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Tramitar */}
      {modalTramitar && projetoSelecionado && (
        <div className="modal-overlay" onClick={() => setModalTramitar(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><ArrowRightLeft size={16} /> Tramitar — {projetoSelecionado.numero}</h2>
              <button className="btn-modal-close" onClick={() => setModalTramitar(false)}><X size={18} /></button>
            </div>
            <form onSubmit={tramitar}>
              <div className="form-group">
                <label className="form-label">Nova Etapa *</label>
                <select className="form-input form-select" value={formTramitar.etapa} onChange={e => setFormTramitar({ ...formTramitar, etapa: e.target.value })} required>
                  {Object.entries(ETAPA_LABEL).filter(([k]) => k !== 'COMISSAO').map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 6, lineHeight: 1.45 }}>
                  Para mandar a matéria a uma comissão, use <b>Encaminhar à comissão</b> na própria matéria: ele já registra no histórico, designa o relator e acompanha o parecer.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Status *</label>
                <select className="form-input form-select" value={formTramitar.status} onChange={e => setFormTramitar({ ...formTramitar, status: e.target.value })}>
                  <option value="EM_TRAMITACAO">Em Tramitação</option>
                  <option value="APROVADO">Aprovado</option>
                  <option value="REPROVADO">Reprovado</option>
                  <option value="PROMULGADO">Promulgado</option>
                  <option value="ARQUIVADO">Arquivado</option>
                  <option value="DEVOLVIDO">Devolvido ao Autor</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Descrição da movimentação *</label>
                <textarea className="form-input" rows={3} value={formTramitar.descricao} onChange={e => setFormTramitar({ ...formTramitar, descricao: e.target.value })} required style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Observação (opcional)</label>
                <textarea className="form-input" rows={2} value={formTramitar.parecer} onChange={e => setFormTramitar({ ...formTramitar, parecer: e.target.value })} style={{ resize: 'vertical' }} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalTramitar(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Tramitar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Vincular Pauta */}
      {modalVincular && projetoSelecionado && (
        <div className="modal-overlay" onClick={() => setModalVincular(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><Link size={16} /> Vincular ao Plenário — {projetoSelecionado.numero}</h2>
              <button className="btn-modal-close" onClick={() => setModalVincular(false)}><X size={18} /></button>
            </div>
            <form onSubmit={vincularPauta}>
              <div className="form-group">
                <label className="form-label">Selecionar Pauta *</label>
                <select className="form-input form-select" value={pautaVincular} onChange={e => setPautaVincular(e.target.value)} required>
                  <option value="">Selecione uma pauta</option>
                  {pautas.map(p => <option key={p.id} value={p.id}>{p.numero} — {p.titulo} ({p.sessaoTitulo})</option>)}
                </select>
                {pautas.length === 0 && <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 5 }}>Nenhuma pauta disponível para vinculação.</p>}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalVincular(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando || pautas.length === 0}>{salvando ? 'Vinculando...' : 'Vincular ao Plenário'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
